import { clamp, divTrunc, isqrt, movementDirection, scaleDirection } from './vectors';

/**
 * One tick of a hull's movement, as a pure function, with no mode in it.
 *
 * ## Where this lived, and why it moved
 *
 * It was `pvpAdvanceShip`, and everything about it was general except three
 * things it read straight out of the duel's `balance.ts`: the arena's width,
 * the arena's height and the arena's pillars. An open-world quadrant is a
 * different box with no pillars in it, so the function could not be reused - and
 * copying it would have produced a second physics that agrees with the first
 * only for as long as nobody edits either.
 *
 * So the three constants became arguments and the arithmetic did not change by
 * one operation. {@link advanceHull} is what runs; `pvp/movement.ts` calls it
 * with the arena's numbers and is otherwise the file it always was.
 *
 * ## Why one implementation matters more than usual here
 *
 * The client PREDICTS its own hull so a key press shows on the next rendered
 * frame rather than up to a tick later. A prediction that used its own speed,
 * its own normalisation or its own collision response would drift from the
 * authority within a second and be corrected forever after - and the drift
 * would be invisible in tests, because each side would be self-consistent.
 *
 * ## Integer, in one space, everywhere
 *
 * Milli-units throughout, truncating division, no floats. A prediction that
 * rounded differently from the authority would produce a permanent one-unit
 * disagreement and a reconciliation that never settles.
 */

export interface MovablePoint {
  readonly x: number;
  readonly y: number;
}

/** A hull that blocks another: where it is, and how much of it there is. */
export interface HullBlocker {
  readonly x: number;
  readonly y: number;
  readonly radius: number;
}

/** An axis-aligned solid a hull is pushed out of. */
export interface MotionBox {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * The box a hull may occupy, from `(0, 0)` to `(width, height)`, less the
 * hull's own radius on every side.
 *
 * An origin is deliberately absent: every space in this project puts its own
 * origin at zero and keeps its offsets local, which is what stops a coordinate
 * growing until its square leaves the exact-integer range.
 */
export interface MotionBounds {
  readonly width: number;
  readonly height: number;
}

/** Nothing. Shared references, so the common case allocates nothing. */
const NO_BLOCKERS: readonly HullBlocker[] = Object.freeze([]);
const NO_BOXES: readonly MotionBox[] = Object.freeze([]);
const ZERO_DRIFT: MovablePoint = Object.freeze({ x: 0, y: 0 });

/**
 * Everything the step needs that is not the hull's own intent.
 *
 * A single object rather than seven positional parameters, because the duel
 * already had five and a world adds bounds: a call site with seven positions is
 * a call site where two of them get swapped and nothing fails to compile.
 */
export interface HullStepRules {
  /** How far an unmodified hull travels in one tick, in milli-units. */
  readonly baseTravelMu: number;
  /** The walls. Solid on all four sides; a hull is clamped inside them. */
  readonly bounds: MotionBounds;
  /**
   * Speed as integer basis points of the base. 10 000 is unmodified.
   *
   * A multiplier rather than a second speed constant, so a bonus cannot drift
   * away from the number it is meant to be a multiple of. Integer maths
   * throughout: a float here would make the authority and the client's
   * prediction disagree in the last bit and correct forever.
   */
  readonly speedBps?: number;
  /**
   * Everything that moves the hull WITHOUT the player asking, per tick.
   *
   * Applied before the walls and the solids rather than after, so a shove can
   * no more push a hull through a pillar than a key can.
   */
  readonly drift?: MovablePoint;
  /** This hull's radius. A bigger hull meets a wall sooner. */
  readonly radius: number;
  /** Fixed solids. Empty in a space that has none, which costs no loop body. */
  readonly boxes?: readonly MotionBox[];
  /**
   * **The other hulls, which this one does not pass through.**
   *
   * The positions are the ones at the START of the tick, and that is what makes
   * it deterministic. If each hull were pushed out of the already-updated
   * positions of the ones before it, the order the room walked them would
   * decide the outcome, and two re-simulations of the same window could
   * diverge. Against the start-of-tick state there is no order: every hull sees
   * the same map.
   *
   * Whoever is moving is the one pushed out, and never the other. A small hull
   * does not shove a large one - it comes to rest against it.
   */
  readonly blockers?: readonly HullBlocker[];
}

/**
 * How far one tick of holding a direction moves a hull, at a given speed.
 *
 * Pulled out so that anything which needs to reason about distance uses the
 * SAME arithmetic rather than a second copy of it. Integer basis points,
 * truncating division: a caller that recomputed this with a float would
 * disagree with the simulation in the last unit, which is the whole reason this
 * file exists.
 */
export function hullTickTravelMu(baseTravelMu: number, speedBps = 10_000): number {
  return divTrunc(baseTravelMu * speedBps, 10_000);
}

/**
 * Where a hull ends up after one tick of holding `moveX`/`moveY`.
 *
 * Only the SIGN of each axis matters - an authority reduces the wire's values
 * to a sign before it does anything else - and a diagonal is normalised, so
 * holding two keys never travels further than holding one.
 *
 * Standing still is returned unchanged, deliberately: a hull that is not moving
 * is not pushed out of anything, which is what keeps one that spawned in
 * contact with geometry from being nudged every tick.
 */
export function advanceHull(
  from: MovablePoint,
  moveX: number,
  moveY: number,
  rules: HullStepRules,
): MovablePoint {
  const drift = rules.drift ?? ZERO_DRIFT;
  const dirX = Math.sign(moveX);
  const dirY = Math.sign(moveY);
  if (dirX === 0 && dirY === 0 && drift.x === 0 && drift.y === 0) {
    return from;
  }

  let x = from.x + drift.x;
  let y = from.y + drift.y;
  if (dirX !== 0 || dirY !== 0) {
    const direction = movementDirection(dirX, dirY);
    // The same arithmetic `hullTickTravelMu` reasons about, by construction.
    const step = scaleDirection(direction, hullTickTravelMu(rules.baseTravelMu, rules.speedBps));
    x += step.x;
    y += step.y;
  }

  /*
   * Solids first, walls second.
   *
   * A space that has solids has to guarantee none of them comes within a hull
   * radius of a wall - the duel's arena does, in `balance.ts` - so a push-out
   * can never land the hull outside the box and the final clamp can never shove
   * it back into a pillar.
   */
  for (const box of rules.boxes ?? NO_BOXES) {
    const pushed = pushOutOfBox(x, y, box, rules.radius);
    x = pushed.x;
    y = pushed.y;
  }
  for (const blocker of rules.blockers ?? NO_BLOCKERS) {
    const pushed = pushOutOfHull(x, y, rules.radius, blocker);
    x = pushed.x;
    y = pushed.y;
  }
  return {
    x: clamp(x, rules.radius, rules.bounds.width - rules.radius),
    y: clamp(y, rules.radius, rules.bounds.height - rules.radius),
  };
}

/**
 * Pushes a hull out of another one, if it got inside it.
 *
 * The separation required is the SUM of the two radii - two hulls touch when
 * their edges meet, not when their centres come within one radius of each
 * other. All the arithmetic is integer, with a single truncation per axis, for
 * the same reason the push out of a solid is: a client predicts its own hull,
 * and a floating-point division would put it one bit away from the authority
 * and correcting forever.
 *
 * ## The axis test is not an optimisation
 *
 * If either axis alone already covers the wanted separation then the squared
 * distance does too, so answering "no push" there is the SAME answer the
 * comparison below would give - and it is the only way this function stays
 * exact in a space wider than a duel's arena, where `dx * dx + dy * dy` between
 * two distant hulls leaves the exact-integer range. Everything that reaches the
 * multiplication has both axes below the wanted separation by construction.
 */
function pushOutOfHull(
  shipX: number,
  shipY: number,
  radius: number,
  blocker: HullBlocker,
): MovablePoint {
  const wanted = radius + blocker.radius;
  if (wanted <= 0) {
    return { x: shipX, y: shipY };
  }
  const dx = shipX - blocker.x;
  const dy = shipY - blocker.y;
  if (dx >= wanted || dx <= -wanted || dy >= wanted || dy <= -wanted) {
    return { x: shipX, y: shipY };
  }
  const gapSquared = dx * dx + dy * dy;
  if (gapSquared >= wanted * wanted) {
    return { x: shipX, y: shipY };
  }
  const gap = isqrt(gapSquared);
  if (gap === 0) {
    /*
     * Two centres at exactly the same point - an overlapping spawn, or a
     * teleport on top of somebody. There is no direction out, so one is CHOSEN,
     * and always the same one: `+x`. An arbitrary but fixed choice is
     * deterministic; anything drawn from chance would not be.
     */
    return { x: blocker.x + wanted, y: blocker.y };
  }
  return {
    x: blocker.x + divTrunc(dx * wanted, gap),
    y: blocker.y + divTrunc(dy * wanted, gap),
  };
}

/**
 * Pushes a hull out of a solid, if it got inside it.
 *
 * The axis test carries the same weight it does above: it answers exactly what
 * the squared comparison would, and it is what keeps the surviving
 * multiplication inside the exact-integer range in a space wider than an arena.
 */
function pushOutOfBox(shipX: number, shipY: number, box: MotionBox, radius: number): MovablePoint {
  const left = box.x;
  const right = box.x + box.w;
  const top = box.y;
  const bottom = box.y + box.h;
  const closestX = clamp(shipX, left, right);
  const closestY = clamp(shipY, top, bottom);
  const dx = shipX - closestX;
  const dy = shipY - closestY;

  if (dx === 0 && dy === 0) {
    /*
     * The hull's centre is inside the box - a fast hull can arrive here in one
     * step. Leave by the nearest face, which is the only exit that does not
     * teleport it across the solid.
     */
    const toLeft = shipX - left + radius;
    const toRight = right - shipX + radius;
    const toTop = shipY - top + radius;
    const toBottom = bottom - shipY + radius;
    const minimum = Math.min(toLeft, toRight, toTop, toBottom);
    if (minimum === toLeft) {
      return { x: left - radius, y: shipY };
    }
    if (minimum === toRight) {
      return { x: right + radius, y: shipY };
    }
    if (minimum === toTop) {
      return { x: shipX, y: top - radius };
    }
    return { x: shipX, y: bottom + radius };
  }

  if (dx >= radius || dx <= -radius || dy >= radius || dy <= -radius) {
    return { x: shipX, y: shipY };
  }
  const gapSquared = dx * dx + dy * dy;
  if (gapSquared >= radius * radius) {
    return { x: shipX, y: shipY };
  }
  const gap = isqrt(gapSquared);
  if (gap === 0) {
    return { x: shipX, y: shipY };
  }
  return {
    x: closestX + divTrunc(dx * radius, gap),
    y: closestY + divTrunc(dy * radius, gap),
  };
}
